import { create } from 'zustand';
import type { Job } from '../api/jobs';

interface JobsState {
  // Active jobs from the API
  activeJobs: Job[];

  // Pending cases (manually tracked, before API confirms)
  pendingDownloads: Set<string>;
  pendingTranslates: Set<string>;
  pendingUploads: Set<string>;
  pendingRestarts: Set<string>;

  // API-confirmed active cases
  apiDownloading: Set<string>;
  apiTranslating: Set<string>;
  apiUploading: Set<string>;
  apiRestarting: Set<string>;

  // Actions
  setActiveJobs: (jobs: Job[]) => void;

  // Immediate tracking (before polling picks up the job)
  addDownloading: (caseNumbers: string[]) => void;
  addTranslating: (caseNumbers: string[]) => void;
  addUploading: (caseNumbers: string[]) => void;
  addRestarting: (caseNumbers: string[]) => void;

  // Clear pending when job is confirmed or completed
  clearPending: (caseNumbers: string[], type: 'download' | 'translate' | 'upload' | 'restart') => void;

  // Helpers - combine pending + API confirmed
  isDownloading: (caseNumber: string) => boolean;
  isTranslating: (caseNumber: string) => boolean;
  isUploading: (caseNumber: string) => boolean;
  isRestarting: (caseNumber: string) => boolean;
  isProcessing: (caseNumber: string) => boolean;
}

export const useJobsStore = create<JobsState>((set, get) => ({
  activeJobs: [],
  pendingDownloads: new Set(),
  pendingTranslates: new Set(),
  pendingUploads: new Set(),
  pendingRestarts: new Set(),
  apiDownloading: new Set(),
  apiTranslating: new Set(),
  apiUploading: new Set(),
  apiRestarting: new Set(),

  setActiveJobs: (jobs) => {
    // Extract case numbers by job type from API
    const downloading = new Set<string>();
    const translating = new Set<string>();
    const uploading = new Set<string>();
    const restarting = new Set<string>();

    for (const job of jobs) {
      if (job.status !== 'queued' && job.status !== 'running') continue;

      const caseNumbers = job.case_numbers || [];

      // Restart jobs trigger a download, so track both restart and download
      if (job.job_type.includes('restart')) {
        caseNumbers.forEach((cn) => restarting.add(cn));
        caseNumbers.forEach((cn) => downloading.add(cn));
      } else if (job.job_type.includes('download')) {
        caseNumbers.forEach((cn) => downloading.add(cn));
      } else if (job.job_type.includes('translate')) {
        caseNumbers.forEach((cn) => translating.add(cn));
      } else if (job.job_type.includes('upload')) {
        caseNumbers.forEach((cn) => uploading.add(cn));
      }
    }

    // Check if anything actually changed before updating
    set((state) => {
      // Compare job IDs
      const oldJobIds = new Set(state.activeJobs.map(j => j.id));
      const newJobIds = new Set(jobs.map(j => j.id));
      const jobsChanged = oldJobIds.size !== newJobIds.size ||
        jobs.some(j => !oldJobIds.has(j.id) || state.activeJobs.find(oj => oj.id === j.id)?.status !== j.status);

      // Compare Sets
      const setsEqual = (a: Set<string>, b: Set<string>) =>
        a.size === b.size && [...a].every(x => b.has(x));

      const downloadingChanged = !setsEqual(state.apiDownloading, downloading);
      const translatingChanged = !setsEqual(state.apiTranslating, translating);
      const uploadingChanged = !setsEqual(state.apiUploading, uploading);
      const restartingChanged = !setsEqual(state.apiRestarting, restarting);

      // If nothing changed, return current state (no re-render)
      if (!jobsChanged && !downloadingChanged && !translatingChanged && !uploadingChanged && !restartingChanged) {
        return state;
      }

      const newPendingDownloads = new Set(state.pendingDownloads);
      const newPendingTranslates = new Set(state.pendingTranslates);
      const newPendingUploads = new Set(state.pendingUploads);
      const newPendingRestarts = new Set(state.pendingRestarts);

      // Remove from pending if API now shows them
      downloading.forEach((cn) => newPendingDownloads.delete(cn));
      translating.forEach((cn) => newPendingTranslates.delete(cn));
      uploading.forEach((cn) => newPendingUploads.delete(cn));
      restarting.forEach((cn) => newPendingRestarts.delete(cn));

      return {
        activeJobs: jobs,
        apiDownloading: downloading,
        apiTranslating: translating,
        apiUploading: uploading,
        apiRestarting: restarting,
        pendingDownloads: newPendingDownloads,
        pendingTranslates: newPendingTranslates,
        pendingUploads: newPendingUploads,
        pendingRestarts: newPendingRestarts,
      };
    });
  },

  // Immediate tracking - add to pending
  addDownloading: (caseNumbers) => {
    set((state) => {
      const newSet = new Set(state.pendingDownloads);
      caseNumbers.forEach((cn) => newSet.add(cn));
      return { pendingDownloads: newSet };
    });
  },

  addTranslating: (caseNumbers) => {
    set((state) => {
      const newSet = new Set(state.pendingTranslates);
      caseNumbers.forEach((cn) => newSet.add(cn));
      return { pendingTranslates: newSet };
    });
  },

  addUploading: (caseNumbers) => {
    set((state) => {
      const newSet = new Set(state.pendingUploads);
      caseNumbers.forEach((cn) => newSet.add(cn));
      return { pendingUploads: newSet };
    });
  },

  addRestarting: (caseNumbers) => {
    set((state) => {
      const newSet = new Set(state.pendingRestarts);
      caseNumbers.forEach((cn) => newSet.add(cn));
      return { pendingRestarts: newSet };
    });
  },

  clearPending: (caseNumbers, type) => {
    set((state) => {
      // If empty array, clear all pending of that type
      const clearAll = caseNumbers.length === 0;

      if (type === 'download') {
        if (clearAll) return { pendingDownloads: new Set() };
        const newSet = new Set(state.pendingDownloads);
        caseNumbers.forEach((cn) => newSet.delete(cn));
        return { pendingDownloads: newSet };
      } else if (type === 'translate') {
        if (clearAll) return { pendingTranslates: new Set() };
        const newSet = new Set(state.pendingTranslates);
        caseNumbers.forEach((cn) => newSet.delete(cn));
        return { pendingTranslates: newSet };
      } else if (type === 'upload') {
        if (clearAll) return { pendingUploads: new Set() };
        const newSet = new Set(state.pendingUploads);
        caseNumbers.forEach((cn) => newSet.delete(cn));
        return { pendingUploads: newSet };
      } else {
        // type === 'restart'
        if (clearAll) return { pendingRestarts: new Set() };
        const newSet = new Set(state.pendingRestarts);
        caseNumbers.forEach((cn) => newSet.delete(cn));
        return { pendingRestarts: newSet };
      }
    });
  },

  // Helpers - combine pending + API confirmed
  isDownloading: (caseNumber) => {
    const state = get();
    return state.pendingDownloads.has(caseNumber) || state.apiDownloading.has(caseNumber);
  },
  isTranslating: (caseNumber) => {
    const state = get();
    return state.pendingTranslates.has(caseNumber) || state.apiTranslating.has(caseNumber);
  },
  isUploading: (caseNumber) => {
    const state = get();
    return state.pendingUploads.has(caseNumber) || state.apiUploading.has(caseNumber);
  },
  isRestarting: (caseNumber) => {
    const state = get();
    return state.pendingRestarts.has(caseNumber) || state.apiRestarting.has(caseNumber);
  },
  isProcessing: (caseNumber) => {
    const state = get();
    return (
      state.pendingDownloads.has(caseNumber) ||
      state.pendingTranslates.has(caseNumber) ||
      state.pendingUploads.has(caseNumber) ||
      state.pendingRestarts.has(caseNumber) ||
      state.apiDownloading.has(caseNumber) ||
      state.apiTranslating.has(caseNumber) ||
      state.apiUploading.has(caseNumber) ||
      state.apiRestarting.has(caseNumber)
    );
  },
}));

// Selector for getting download job progress
export const selectDownloadProgress = (state: JobsState) => {
  const downloadJobs = state.activeJobs.filter(
    (job) => job.job_type.includes('download') &&
             (job.status === 'queued' || job.status === 'running')
  );

  if (downloadJobs.length === 0) return null;

  const totalCases = downloadJobs.reduce(
    (sum, job) => sum + (job.case_numbers?.length || 0),
    0
  );
  const completed = downloadJobs.reduce(
    (sum, job) => sum + (job.progress || 0),
    0
  );

  return { total: totalCases, completed, jobs: downloadJobs };
};
